import interpolateComponents from 'interpolate-components';
import React, { useEffect } from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Notification } from 'api/userNotifications';
import Box from 'components/Box';
import { Link } from 'components/RouterLink';
import { StatusTag } from 'components/StatusLabel';
import Text from 'components/Text';
import UserNotification from 'components/UserNotification';
import { colors, fontWeights } from 'theme';
import { trackClicked, trackEvent } from 'utils/analytics';

const StyledLink = styled(Link)`
  font-weight: ${fontWeights.semiBold};
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const NotificationContent = ({
  notification,
  dismiss,
}: {
  notification: Notification;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dismiss: (...args: Array<any>) => any;
}) => {
  switch (notification.type) {
    case 'NEW_APPLICATIONS_BY_LISTING':
      return (
        <>
          <StyledLink
            onClick={() => {
              trackClicked('User Menu Notification', {
                notification_type: notification.type,
                listing_id: notification.data.id,
                listing_type: notification.data.type,
                listing_name: notification.data.name,
                org_id: notification.data.orgId,
                application_count: notification.data.applications.length,
              });
              dismiss();
            }}
            to={`/admin/${
              notification.data.orgId
            }/${notification.data.type.toLowerCase()}s/${notification.data.id}`}
          >
            {notification.data.name}
          </StyledLink>
          <Box mt="4px">
            {interpolateComponents({
              mixedString: sprintf(
                notification.data.count > 1
                  ? getText(
                      '{{strong}}%(count)s{{/strong}} unread applications'
                    )
                  : getText(
                      '{{strong}}%(count)s{{/strong}} unread application'
                    ),
                {
                  count: notification.data.count,
                }
              ),
              components: {
                strong: <strong />,
              },
            })}
          </Box>
          {notification.data.newCount > notification.data.count && (
            <Box mt="4px" color={colors.battleshipGrey}>
              {interpolateComponents({
                mixedString: sprintf(
                  getText('%(count)s still marked {{status}}new{{/status}}'),
                  {
                    count: notification.data.newCount,
                  }
                ),
                components: {
                  status: (
                    <StatusTag
                      ml="3px"
                      color={colors.waterBlue}
                      boxShadow={`0 0 0 1px ${colors.silver}`}
                    />
                  ),
                },
              })}
            </Box>
          )}
        </>
      );

    case 'NEW_MENTIONS_BY_APPLICATION': {
      const title =
        notification.data.listing.name.length > 70
          ? `${notification.data.listing.name.slice(0, 70)}..`
          : notification.data.listing.name;
      return (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          <StyledLink
            onClick={() => {
              trackClicked('User Menu Notification', {
                notification_type: notification.type,
                application_id: notification.data.id,
                listing_id: notification.data.listing.id,
                listing_type: notification.data.listing.type,
                listing_name: notification.data.listing.name,
                org_id: notification.data.orgId,
                mentions_count: notification.data.count,
              });
              dismiss();
            }}
            to={`/admin/${notification.data.orgId}/applications/${notification.data.id}`}
          >
            {notification.data.count === 1
              ? `${notification.data.mentionBy.name} wants you to review an applicant for ${title}`
              : `Your teammates want you to review an applicant for ${title}`}
          </StyledLink>
        </>
      );
    }

    default:
      return null;
  }
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Notifications = ({
  notifications,
  dismiss,
  open,
}: {
  notifications: Array<Notification>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dismiss: (...args: Array<any>) => any;
  open: boolean;
}) => {
  const primaryNotifications = notifications.slice(0, 3);
  const otherNotifications =
    notifications.length > 3 ? notifications.slice(3) : null;
  const otherListingApplicationNotifications = otherNotifications
    ? otherNotifications.filter(
        (notification) => notification.type === 'NEW_APPLICATIONS_BY_LISTING'
      )
    : null;
  const otherListingApplicationData =
    otherListingApplicationNotifications &&
    otherListingApplicationNotifications.length >= 1 &&
    otherListingApplicationNotifications[0].type ===
      'NEW_APPLICATIONS_BY_LISTING'
      ? otherListingApplicationNotifications[0].data
      : null;
  const otherApplicationMentionNotifications = otherNotifications
    ? otherNotifications.filter(
        (notification) => notification.type === 'NEW_MENTIONS_BY_APPLICATION'
      )
    : null;
  const otherApplicationMentionData =
    otherApplicationMentionNotifications &&
    otherApplicationMentionNotifications.length >= 1 &&
    otherApplicationMentionNotifications[0].type ===
      'NEW_MENTIONS_BY_APPLICATION'
      ? otherApplicationMentionNotifications[0].data
      : null;
  useEffect(() => {
    if (open && notifications.length) {
      trackEvent('Displayed User Menu Notifications', {
        notification_types: notifications.reduce(
          (acc, notification) =>
            acc.includes(notification.type) ? acc : [...acc, notification.type],
          []
        ),
      });
    }
  }, [notifications, open]);
  return (
    <>
      {primaryNotifications.map((notification) => {
        const dismissNotification = () => {
          let ids = [];

          if (notification.type === 'NEW_APPLICATIONS_BY_LISTING') {
            ids = notification.data.applications.map(
              (application) => application.notificationId
            );
          } else if (notification.type === 'NEW_MENTIONS_BY_APPLICATION') {
            ids = notification.data.mentions.map(
              (mention) => mention.notificationId
            );
          } else {
            ids = [notification.id];
          }

          dismiss(ids);
        };

        return (
          <Box
            p={12}
            minWidth={260}
            borderTop={`1px solid ${colors.aluminum}`}
            key={
              notification.type === 'NEW_APPLICATIONS_BY_LISTING' ||
              notification.type === 'NEW_MENTIONS_BY_APPLICATION'
                ? notification.data.id
                : notification.id
            }
          >
            <UserNotification dismiss={dismissNotification}>
              <NotificationContent
                notification={notification}
                dismiss={dismissNotification}
              />
            </UserNotification>
          </Box>
        );
      })}
      {otherListingApplicationNotifications &&
        otherListingApplicationNotifications.length >= 1 &&
        otherListingApplicationData && (
          <Box p={12} minWidth={260} borderTop={`1px solid ${colors.aluminum}`}>
            <Link
              to={`/admin/${otherListingApplicationData.orgId}${
                otherListingApplicationNotifications.length === 1
                  ? `/${otherListingApplicationData.type.toLowerCase()}s/${
                      otherListingApplicationData.id
                    }`
                  : ''
              }`}
            >
              <Text fontWeight={fontWeights.semiBold}>
                {sprintf(
                  otherListingApplicationNotifications.length > 1
                    ? getText(
                        '%(count)s more listings with unread applications'
                      )
                    : getText(
                        '%(count)s more listing with unread applications'
                      ),
                  {
                    count: otherListingApplicationNotifications.length,
                  }
                )}
              </Text>
            </Link>
          </Box>
        )}

      {otherApplicationMentionNotifications &&
        otherApplicationMentionNotifications.length >= 1 &&
        otherApplicationMentionData && (
          <Box p={12} minWidth={260} borderTop={`1px solid ${colors.aluminum}`}>
            <Link
              to={`/admin/${otherApplicationMentionData.orgId}${
                otherApplicationMentionNotifications.length === 1
                  ? `/applications/${otherApplicationMentionData.id}`
                  : ''
              }`}
            >
              <Text fontWeight={fontWeights.semiBold}>
                {sprintf(
                  otherApplicationMentionNotifications.length > 1
                    ? getText(
                        '%(count)s more applications with unread mentions'
                      )
                    : getText(
                        '%(count)s more application with unread mentions'
                      ),
                  {
                    count: otherApplicationMentionNotifications.length,
                  }
                )}
              </Text>
            </Link>
          </Box>
        )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default Notifications;
